import classNames from "classnames";
import Segmented, { SegmentedLabeledOption } from "rc-segmented";
import { useEffect, useRef } from "react";
import "./kuika-tabs.scss";

export interface KuikaTabOption {
  value: string;
  icon?: React.ReactNode;
  label?: string | React.ReactNode;
  // rc-segmented does not forward arbitrary props to an option, so an option that
  // needs a stable hook gets it by wrapping its label.
  dataTestId?: string;
  /** Disables this one tab (rc-segmented per-option disabled); the others stay clickable. */
  disabled?: boolean;
  /** Native `title` tooltip — the reliable hover explanation for a disabled tab. */
  title?: string;
}

export type KuikaTabsSize = "sm" | "md" | "lg";

interface IKuikaTabsProps {
  options: (string | KuikaTabOption)[];
  dataTestId?: string;
  onChange: (value: string) => void;
  value?: string;
  defaultValue?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Size on the control scale. Tabs have no md rung of their own: the default and `sm` are the 24px
   * option (a 28px control with the track); `lg` is a 32px control — the md rung, so the bar lines
   * up with a 32px input — around a 28px option. `md` renders as the default; it is accepted so
   * existing call sites keep compiling.
   */
  size?: KuikaTabsSize;
  /**
   * Names the group for assistive tech: rc-segmented draws a `radiogroup` called "segmented
   * control" otherwise, which says nothing about what is being chosen.
   */
  ariaLabel?: string;
}

/**
 * Segmented control. The pill slides between options on the layout spring; under reduced motion it
 * appears at the new option. It only selects — pair it with `KuikaTabContent` to swap the content.
 */
const KuikaTabs = ({
  options,
  dataTestId,
  onChange,
  value,
  defaultValue,
  className,
  disabled,
  size,
  ariaLabel
}: IKuikaTabsProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // rc-segmented puts `title` on the option's inner label div — but that option's own radio
  // input sits on top of it (position: absolute, z-index 3, covering the whole option, so it
  // stays the click target) and is what the mouse actually hovers, so the div's title never
  // shows a tooltip. Set it on the <label class="rc-segmented-item"> itself instead: the browser
  // walks up from whatever is actually hovered to the nearest ancestor carrying `title`.
  useEffect((): void => {
    const container: HTMLDivElement | null = containerRef.current;
    if (!container) {
      return;
    }
    const labels: NodeListOf<HTMLLabelElement> = container.querySelectorAll<HTMLLabelElement>(".rc-segmented-item");
    options.forEach((option: string | KuikaTabOption, index: number): void => {
      const label: HTMLLabelElement | undefined = labels[index];
      if (!label) {
        return;
      }
      const titleText: string | undefined = typeof option === "object" ? option.title : undefined;
      if (titleText) {
        label.setAttribute("title", titleText);
      } else {
        label.removeAttribute("title");
      }
    });
  }, [options]);

  const processedOptions: (string | SegmentedLabeledOption)[] = options.map(
    (option: string | KuikaTabOption): string | SegmentedLabeledOption => {
      if (typeof option === "string") {
        return option;
      }
      const content: React.ReactNode = option.icon || option.label;
      return {
        value: option.value,
        label: option.dataTestId ? <span data-testid={option.dataTestId}>{content}</span> : content,
        ...(option.disabled !== undefined ? { disabled: option.disabled } : {}),
        ...(option.title !== undefined ? { title: option.title } : {})
      };
    }
  );

  return (
    <Segmented
      ref={containerRef}
      className={classNames("kuika-tabs", size === "lg" ? "kuika-tabs--lg" : "kuika-tabs--sm", className)}
      data-testid={dataTestId}
      options={processedOptions}
      onChange={(nextValue: string | number): void => onChange(nextValue as string)}
      {...(value !== undefined ? { value } : {})}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
    />
  );
};

export default KuikaTabs;
